// DEPENDENCIES ---------------------------------------------------------------- //

import React, { useMemo } from 'react';

import { css } from '@emotion/css';
import { Container, Theme, useTheme } from '@mui/material';
import { PiSteeringWheelFill } from 'react-icons/pi';

import Toolbar from '@components/Utils/Toolbar';
import { useFeaturePermission } from '@utils/featurePermissions';
import { FeatureName } from '@utils/featureNames';
import { Tab } from 'src/types';
import { useCustomerScope } from '../hooks/useCustomerScope';

import TeamTabContent from '../components/TeamTabContent';
import DriversTabContent from '../components/DriversTabContent';
import AccessTabContent from '../components/AccessTabContent';

// COMPONENT ---------------------------------------------------------------- //

const TeamDashboardPage = () => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const canManageDrivers = useFeaturePermission(FeatureName.DRIVER_MANAGEMENT);

  const { customerIds, customers, hasDriverCapableRooftop } = useCustomerScope();

  const showDrivers = canManageDrivers && hasDriverCapableRooftop;

  // Tab config
  const [activeTab, setActiveTab] = React.useState(0);

  const tabs: Tab[] = useMemo(() => {
    const t: Tab[] = [
      { id: 0, label: 'Users', icon: 'group' },
    ];
    if (showDrivers) {
      t.push({ id: 1, label: 'Drivers', icon: <PiSteeringWheelFill style={{ fontSize: 20, marginBottom: -2 }} /> });
    }
    t.push({ id: 2, label: 'Access', icon: 'security' });
    return t;
  }, [showDrivers]);

  // Reset to team tab if active tab becomes hidden
  React.useEffect(() => {
    if (!tabs.find(t => t.id === activeTab)) {
      setActiveTab(0);
    }
  }, [tabs, activeTab]);

  const handleRefetch = async () => {
    // Triggers re-render which refetches queries via Apollo cache policy
  };

  return (
    <>
      <div className={cls.header}>
        <Toolbar
          fullscreen
          title="Users"
          refetch={handleRefetch}
          tabs={tabs}
          activeTab={activeTab}
          onActiveTabChange={(tabId) => setActiveTab(tabId ?? 0)}
        />
      </div>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Tab content */}
        {activeTab === 0 && <TeamTabContent customerIds={customerIds} />}
        {activeTab === 1 && showDrivers && <DriversTabContent customerIds={customerIds} />}
        {activeTab === 2 && <AccessTabContent customerIds={customerIds} customers={customers} />}
      </Container>
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  header: css`
    background-color: ${theme.palette.background.paper};
    border-bottom: 1px solid ${theme.palette.divider};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default TeamDashboardPage;
